/**
 * Utilities for workflow integration, state management, and persistence
 */

import { SemanticFramework } from '@/services';
import { AnalysisData } from './analysis';
import { debugLog } from './debug';

/**
 * Workflow parameter interface for URL parameters
 */
export interface WorkflowParams {
  inputBlobPath?: string
  clientId?: string
  batchSet?: string
  jobId?: string
  outputBlobPath?: string
}

/**
 * Workflow state interface for persistence
 */
export interface WorkflowState {
  workflowId: string | null;
  currentStep: number;
  workflowData: {
    inputBlobPath?: string;
    analysisResult?: AnalysisData;
    semanticResult?: SemanticFramework;
    fileName?: string;
    clientId?: string;
    batchName?: string;
    frameworkId?: string;
    jobId?: string;
    modelId?: string;
    codeId?: string;
    workflowPath?: string;
    clientCode?: string;
    externalId?: string;
    // Approval-specific fields
    topicFrameworkApproved?: boolean;
    codeApproved?: boolean;
    semanticFrameworkApproved?: boolean;
    // Timestamps for tracking
    createdAt?: string;
    lastUpdatedAt?: string;
  };
  approvalHistory: ApprovalHistoryEntry[];
}

export interface ApprovalHistoryEntry {
  timestamp: string;
  module: 'module1' | 'module2' | 'module3' | 'module4';
  action: 'approved' | 'rejected' | 'modified';
  comment?: string;
  userId?: string;
}

// Storage Keys
const WORKFLOW_STATE_KEY = 'keyword_classifier_workflow_state';
const WORKFLOW_HISTORY_KEY = 'keyword_classifier_workflow_history';

// Default initial state
export const initialWorkflowState: WorkflowState = {
  workflowId: null,
  currentStep: 0,
  workflowData: {},
  approvalHistory: []
};

/**
 * Parse workflow parameters from URL search params
 */
export const parseWorkflowParams = (search: string): WorkflowParams => {
  const params = new URLSearchParams(search)
  
  return {
    inputBlobPath: params.get('inputBlobPath') || undefined,
    clientId: params.get('clientId') || undefined,
    batchSet: params.get('batchSet') || undefined,
    jobId: params.get('jobId') || undefined,
    outputBlobPath: params.get('outputBlobPath') || undefined
  }
}

/**
 * Create a URL for the next workflow step
 */
export const createWorkflowContinuationUrl = (
  nextStep: string,
  params: WorkflowParams
): string => {
  const searchParams = new URLSearchParams()
  
  // Add all non-undefined parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.set(key, value)
    }
  })
  
  return `${nextStep}?${searchParams.toString()}`
}

/**
 * Validate workflow parameters
 */
export const validateWorkflowParams = async (
  params: WorkflowParams,
  _validateBlob = true
): Promise<{ isValid: boolean; error?: string }> => {
  // Basic validation
  if (!params.inputBlobPath && !params.jobId) {
    return { 
      isValid: false, 
      error: 'Missing required workflow parameters: inputBlobPath or jobId' 
    }
  }
  
  // More detailed validation could be added here
  // For example, checking if the blob exists, validating clientId format, etc.
  
  return { isValid: true }
}

/**
 * Save workflow state to localStorage
 */
export const saveWorkflowState = (state: WorkflowState): void => {
  try {
    // Update the last updated timestamp
    const stateWithTimestamp = {
      ...state,
      workflowData: {
        ...state.workflowData,
        lastUpdatedAt: new Date().toISOString()
      }
    };
    
    // Save to localStorage
    localStorage.setItem(WORKFLOW_STATE_KEY, JSON.stringify(stateWithTimestamp));
    debugLog('WorkflowStorage', 'Saved workflow state', stateWithTimestamp);
  } catch (error) {
    console.error('Failed to save workflow state:', error);
  }
};

/**
 * Load workflow state from localStorage
 */
export const loadWorkflowState = (): WorkflowState => {
  try {
    const savedState = localStorage.getItem(WORKFLOW_STATE_KEY);
    
    if (!savedState) {
      return { ...initialWorkflowState };
    }
    
    const parsedState = JSON.parse(savedState) as WorkflowState;
    debugLog('WorkflowStorage', 'Loaded workflow state', parsedState);
    return parsedState;
  } catch (error) {
    console.error('Failed to load workflow state:', error);
    return { ...initialWorkflowState };
  }
};

/**
 * Clear the workflow state from localStorage
 */
export const clearWorkflowState = (): void => {
  try {
    // Archive the current workflow before clearing
    archiveCurrentWorkflow();
    
    // Clear current state
    localStorage.removeItem(WORKFLOW_STATE_KEY);
    debugLog('WorkflowStorage', 'Cleared workflow state');
  } catch (error) {
    console.error('Failed to clear workflow state:', error);
  }
};

/**
 * Add an approval history entry
 */
export const addApprovalHistoryEntry = (
  state: WorkflowState,
  entry: Omit<ApprovalHistoryEntry, 'timestamp'>
): WorkflowState => {
  const newEntry: ApprovalHistoryEntry = {
    ...entry,
    timestamp: new Date().toISOString()
  };
  
  const updatedState = {
    ...state,
    approvalHistory: [...state.approvalHistory, newEntry]
  };
  
  saveWorkflowState(updatedState);
  return updatedState;
};

/**
 * Archive the current workflow to history before starting a new one
 */
export const archiveCurrentWorkflow = (): void => {
  try {
    const currentState = loadWorkflowState();
    
    // Only archive if there's an actual workflow
    if (!currentState.workflowId) {
      return;
    }
    
    // Get existing history
    const existingHistory = localStorage.getItem(WORKFLOW_HISTORY_KEY);
    const workflowHistory = existingHistory ? JSON.parse(existingHistory) : [];
    
    // Add current workflow to history
    workflowHistory.push({
      ...currentState,
      archivedAt: new Date().toISOString()
    });
    
    // Save history (limit to last 10 workflows)
    localStorage.setItem(
      WORKFLOW_HISTORY_KEY, 
      JSON.stringify(workflowHistory.slice(-10))
    );
    
    debugLog('WorkflowStorage', 'Archived workflow', currentState.workflowId);
  } catch (error) {
    console.error('Failed to archive workflow:', error);
  }
};

/**
 * Get workflow history from localStorage
 */
export const getWorkflowHistory = (): WorkflowState[] => {
  try {
    const history = localStorage.getItem(WORKFLOW_HISTORY_KEY);
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('Failed to get workflow history:', error);
    return [];
  }
};

/**
 * Get a specific workflow from history by ID
 */
export const getWorkflowFromHistory = (workflowId: string): WorkflowState | null => {
  try {
    const history = getWorkflowHistory();
    return history.find(workflow => workflow.workflowId === workflowId) || null;
  } catch (error) {
    console.error('Failed to get workflow from history:', error);
    return null;
  }
};

/**
 * Restore a workflow from history to current state
 */
export const restoreWorkflowFromHistory = (workflowId: string): boolean => {
  try {
    const workflow = getWorkflowFromHistory(workflowId);
    
    if (!workflow) {
      return false;
    }
    
    // Save the workflow to current state
    saveWorkflowState(workflow);
    return true;
  } catch (error) {
    console.error('Failed to restore workflow from history:', error);
    return false;
  }
};

export default {
  parseWorkflowParams,
  createWorkflowContinuationUrl,
  validateWorkflowParams,
  saveWorkflowState,
  loadWorkflowState,
  clearWorkflowState,
  addApprovalHistoryEntry,
  getWorkflowHistory,
  getWorkflowFromHistory,
  restoreWorkflowFromHistory
};